/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box, Tooltip, InputAdornment, IconButton } from "@mui/material"
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { MyInput } from "../../../components/input/MyInput"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 100%;
    
    ${mobileCss(`
        gap: 1.5rem;
    `)}
`)

const PartnerSectionStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
`)

const PartnerNameStyle = withMyTheme((theme) => css`
    font-size: 1.2rem;
    font-weight: 600;
    color: ${theme.palette.primary.dark};
    font-family: ${theme.typography.body1.fontFamily};
    text-align: left;

    ${mobileCss(`
        font-size: 1.1rem;
    `)}
`)

const InfoTooltipStyle = withMyTheme((theme) => css`
    color: ${theme.palette.primary.main};
    padding: 4px;
`)

const infoAdornment = (tooltip: string) => ({
    endAdornment: (
        <InputAdornment position="end">
            <Tooltip title={tooltip} arrow enterTouchDelay={0} leaveTouchDelay={3000}>
                <IconButton css={InfoTooltipStyle} size="small" tabIndex={-1}>
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </InputAdornment>
    )
})

interface ContactStepProps {
    onStart?: () => void
}

export const ContactStep = ({}: ContactStepProps) => {
    const { t } = useTranslation()
    const { formData, updateContact } = useQuestionnaireForm()

    const name1 = formData.basicInfo.coupleName1
    const name2 = formData.basicInfo.coupleName2

    const handleEmail1Change = (value: string) => {
        updateContact({ email1: value })
    }

    const handlePhone1Change = (value: string) => {
        updateContact({ phone1: value })
    }

    const handleEmail2Change = (value: string) => {
        updateContact({ email2: value })
    }

    const handlePhone2Change = (value: string) => {
        updateContact({ phone2: value })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.contact.title')}
            </Typography>
            
            <Box css={FormContainerStyle}>
                <Box css={PartnerSectionStyle}>
                    {name1 && (
                        <Typography css={PartnerNameStyle}>{name1}</Typography>
                    )}
                    <MyInput
                        label={name1 ? t('questionnaire.contact.emailLabel', { name: name1 }) : t('questionnaire.contact.email1Label')}
                        placeholder={name1 ? t('questionnaire.contact.emailPlaceholder', { name: name1 }) : t('questionnaire.contact.email1Placeholder')}
                        value={formData.contact.email1}
                        onChange={handleEmail1Change}
                        InputProps={infoAdornment(t('questionnaire.contact.emailTooltip'))}
                    />
                    <MyInput
                        label={name1 ? t('questionnaire.contact.phoneLabel', { name: name1 }) : t('questionnaire.contact.phone1Label')}
                        placeholder={name1 ? t('questionnaire.contact.phonePlaceholder', { name: name1 }) : t('questionnaire.contact.phone1Placeholder')}
                        value={formData.contact.phone1}
                        onChange={handlePhone1Change}
                        InputProps={infoAdornment(t('questionnaire.contact.phoneTooltip'))}
                    />
                </Box>

                <Box css={PartnerSectionStyle}>
                    {name2 && (
                        <Typography css={PartnerNameStyle}>{name2}</Typography>
                    )}
                    <MyInput
                        label={name2 ? t('questionnaire.contact.emailLabel', { name: name2 }) : t('questionnaire.contact.email2Label')}
                        placeholder={name2 ? t('questionnaire.contact.emailPlaceholder', { name: name2 }) : t('questionnaire.contact.email2Placeholder')}
                        value={formData.contact.email2}
                        onChange={handleEmail2Change}
                        InputProps={infoAdornment(t('questionnaire.contact.emailTooltip'))}
                    />
                    <MyInput
                        label={name2 ? t('questionnaire.contact.phoneLabel', { name: name2 }) : t('questionnaire.contact.phone2Label')}
                        placeholder={name2 ? t('questionnaire.contact.phonePlaceholder', { name: name2 }) : t('questionnaire.contact.phone2Placeholder')}
                        value={formData.contact.phone2}
                        onChange={handlePhone2Change}
                        InputProps={infoAdornment(t('questionnaire.contact.phoneTooltip'))}
                    />
                </Box>
            </Box>
        </div>
    )
}
